import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createIconElement } from '@/lib';
import styles from './StanDropdownMenu.module.css';
import { DropdownMenuOption, DropdownMenuProps } from '../StanDropdown.types';
import { useDropdown } from '../DropdownProvider';
import {
    StanFloatingMenu,
    type StanFloatingMenuHandle,
} from '@/components/core-components/StanDropdown/FloatingMenu/StanFloatingMenu';
import { cn } from '@/lib/utils/cn';

export const StanDropdownMenu = ({
    id,
    name,
    options,
    onSelect,
    children,
    className = '',
    placement = 'bottom-end',
    offset = 8,
    minWidth = 180,
    currentValue,
    keepOpen = false,
    rounded = false,
    disabled = false,
}: DropdownMenuProps) => {
    const { handleSelect, setIsOpen, isOpen } = useDropdown();
    const triggerContainerRef = useRef<HTMLDivElement | null>(null);
    const floatingMenuRef = useRef<StanFloatingMenuHandle | null>(null);
    const listRef = useRef<HTMLDivElement | null>(null);
    const [triggerWidth, setTriggerWidth] = useState<number>(minWidth);

    useEffect(() => {
        const node = triggerContainerRef.current;
        if (!node) return;

        const updateWidth = () => {
            const width = node.getBoundingClientRect().width;
            if (width) {
                setTriggerWidth(Math.max(width, minWidth));
            }
        };

        updateWidth();

        const resizeObserver = new ResizeObserver(updateWidth);
        resizeObserver.observe(node);

        return () => {
            resizeObserver.disconnect();
        };
    }, [minWidth]);

    const computedMinWidth = useMemo(() => Math.max(triggerWidth, minWidth), [minWidth, triggerWidth]);

    // Scroll to selected item when menu opens
    useEffect(() => {
        if (isOpen && listRef.current && currentValue) {
            // Use setTimeout to ensure the DOM has rendered
            setTimeout(() => {
                const selectedButton = listRef.current?.querySelector(`.${styles.selected}`);
                if (selectedButton) {
                    selectedButton.scrollIntoView({
                        block: 'center',
                    });
                }
            }, 0);
        }
    }, [isOpen, currentValue]);

    const handleOptionSelect = useCallback(
        (option: DropdownMenuOption) => {
            handleSelect(option, onSelect, keepOpen);

            if (!keepOpen) {
                floatingMenuRef.current?.setMenu(false);
            }
        },
        [handleSelect, keepOpen, onSelect]
    );

    const renderOption = useCallback(
        (option: DropdownMenuOption) => {
            if (option.type === 'divider') {
                return <div key={`divider-${option.value}`} className={styles.divider} />;
            }

            const IconComponent =
                option.icon && typeof option.icon !== 'string' ? createIconElement(option.icon) : null;
            const ImageComponent =
                option.icon && typeof option.icon === 'string' ? (
                    <img src={option.icon} alt={option.label} className={styles.imageWrapper} />
                ) : null;

            const isSelected = currentValue?.value === option.value;

            return (
                <button
                    key={option.value}
                    className={cn(
                        styles.item,
                        isSelected && styles.selected,
                        option.disabled && styles.disabled,
                        option.type === 'destructive' && styles.destructive,
                        option.className
                    )}
                    onClick={() => handleOptionSelect(option)}
                    disabled={option.disabled}
                    type='button'
                >
                    <div className={styles.itemContent}>
                        {IconComponent && <div className={styles.iconWrapper}>{IconComponent}</div>}
                        {ImageComponent && <div className={styles.iconWrapper}>{ImageComponent}</div>}
                        <span className={styles.itemLabel}>{option.label}</span>
                    </div>
                </button>
            );
        },
        [currentValue?.value, handleOptionSelect]
    );

    const trigger = useMemo(() => <div className={styles.trigger}>{children}</div>, [children]);

    return (
        <div
            ref={triggerContainerRef}
            className={cn(styles.wrapper, className, rounded ? styles.rounded : '')}
            id={`dropdown-wrapper-${id}`}
        >
            <StanFloatingMenu
                id={`${id}-menu`}
                name={name}
                ref={floatingMenuRef}
                rounded={rounded}
                placement={placement}
                offset={offset}
                minWidth={computedMinWidth}
                keepOpen={keepOpen}
                disabled={disabled}
                onOpenChange={setIsOpen}
                trigger={trigger}
            >
                <div ref={listRef} className={styles.list}>
                    {options.map(renderOption)}
                </div>
            </StanFloatingMenu>
        </div>
    );
};
